import { createSlice, createSelector, createAsyncThunk } from "@reduxjs/toolkit";

export const fetchQueue = createAsyncThunk(
    'patientQueue/fetchQueue',
        async(_, thunkAPI) => {
            try{
                const res = await fetch('/api/v1/patient_queues');
                if(!res.ok) throw new Error(`HTTP ${res.status}`);
                return await res.json();
            }  catch (err) {
                return thunkAPI.rejectWithValue(err.message);
            }
        }
);

export const fetchCompletedQueues = createAsyncThunk(
  "patientQueue/fetchCompletedQueues",
  async (_, thunkAPI) => {
    try {
      const res = await fetch("/api/v1/patient_queues/completed");
      if (!res.ok) throw new Error("Failed to fetch completed queues");
      return await res.json();
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const addToQueue = createAsyncThunk(
    'patientQueue/addToQueue',
    async ( {patientId, intent} , thunkAPI) => {
      try{
        const res = await fetch(`/api/v1/patient_queues`,{
            method: "POST",
            headers: { "Content-Type": "application/json"},
            body: JSON.stringify({ patient_queue: {patient_id: patientId , intent}})
        });
        if(!res.ok) throw new Error("Failed to add to queue");
        return await res.json();        
    }
    catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const updateQueueStatus = createAsyncThunk (
    'patientQueue/updateStatus',
    async({id, status}, thunkAPI) => {
        try {
            const res = await fetch(`/api/v1/patient_queues/${id}`,{
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({ patient_queue: {status}})
            });
            if (!res.ok) throw new Error("Update failed");
            return await res.json();
         } catch (err){
            return thunkAPI.rejectWithValue(err.message);
         }
    }
);

export const callInPatient = createAsyncThunk(
    "patientQueue/callIn",
    async(id, thunkAPI) => {
        try {
            const res = await fetch (`/api/v1/patient_queues/${id}/call_in`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json"},
            });
            if(!res.ok) throw new Error ("Update failed");
            return await res.json();
        }catch(err){
          return  thunkAPI.rejectWithValue(err.message);
        }
    }
);

export const removeQueueEntry = createAsyncThunk(
  'patientQueue/removeQueueEntry',
  async (id, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/patient_queues/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to remove queue entry");
      return {id}; // returns updated queue_entry
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);


const initialState = {
    list: [],
    completedList: [],   
    loading: false,
    error: null,
  }
const patientQueueSlice = createSlice({
    name: 'patientQueue',
    initialState,
    reducers: {
    setCallingIn: (state, action) => {
        const entryId = action.payload;
        const idx = state.list.findIndex(e => e.id === entryId);
        if (idx !== -1) {
          state.list[idx].callingIn = true; // temporary state
        }
      },
      clearCallingIn: (state, action) => {
        const entryId = action.payload;
        const idx = state.list.findIndex(e => e.id === entryId);
        if (idx !== -1) {
          state.list[idx].callingIn = false;
        }
      },
        updateQueue: (state, action) => {
           const {actionType, entry} = action.payload;

           if (entry?.active === false) {
                state.list = state.list.filter(e => e.id !== entry.id);
                return;
           }

            switch (actionType) {
                case 'create': {
                  const exists = state.list.find((e) => Number(e.id) === Number(entry.id));
                  if (!exists) state.list.push(entry);
                  break;
                }

                case 'update': {
                    // console.log('Updating queue entry', entry);
                    const idx = state.list.findIndex((e) => Number(e.id) === Number(entry.id));
                    if (idx !== -1) {
                        // Merge new fields with existing to preserve previous values
                        state.list[idx] = { ...state.list[idx], ...entry };
                    } else {
                        state.list.push(entry);
                    }
                    break;
                }

                 case 'destroy': {
                    state.list = state.list.filter(e => e.id !== entry.id);
                    break;
                  }
              default:
                  break;
            }

        },        
      },
    extraReducers: (builder) => {
        builder
          .addCase(fetchQueue.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(fetchQueue.fulfilled, (state,action) => {
            state.loading = false;
            state.list = action.payload;
          })
          .addCase(fetchQueue.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch Queue";
          })
        .addCase(updateQueueStatus.fulfilled, (state, action) => {
            const updated = action.payload;
            const idx = state.list.findIndex(e => e.id === updated.id);
            if (idx !== -1) {
                state.list[idx] = { ...state.list[idx], ...updated };
            }
        })
          .addCase(callInPatient.fulfilled, (state, action) => {
                const updated = action.payload;
                const idx = state.list.findIndex(e => e.id === updated.id);
                if (idx !== -1) {
                    state.list[idx] = { ...state.list[idx], ...updated };
                }
             })

             .addCase(removeQueueEntry.fulfilled, (state, action) => {
                state.list = state.list.filter(e => e.id !== action.payload.id);
              })
              .addCase(fetchCompletedQueues.pending, (state) => {
                state.loading = true;
                state.error = null;
              })
              .addCase(fetchCompletedQueues.fulfilled, (state, action) => {
                state.loading = false;
                state.completedList = action.payload;
              })
              .addCase(fetchCompletedQueues.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
              });
        }
    });

export const { updateQueue, setCallingIn, clearCallingIn } = patientQueueSlice.actions;
const selectList = (state) => state.patientQueue.list;
export const selectQueue = createSelector(
  [selectList],
  (list) => list.filter((entry) => entry.active)
);
export const selectQueueLoading = (state) => state.patientQueue.loading;
export const selectQueueError = (state) => state.patientQueue.error;
export const selectCompletedQueues = (state) =>
  state.patientQueue.completedList;


export default patientQueueSlice.reducer;
